import { Alert, Box, Card, DialogActions, DialogContent, Grid } from "@mui/material";
import { MprcFormSubHeader, MprcLink, MprcRequestIndicator } from "components";
import { ButtonCancel, ButtonSave } from "components/buttons";
import { IconAdd, IconUndo } from "components/icons";
import { IconSubtract } from "components/icons/IconSubtract";
import { MprcButton } from "components/MprcButton";
import { MprcCardPrimaryText } from "components/MprcCardPrimaryText";
import { MprcContainer } from "components/MprcContainer";
import { MprcIconButtonDropdown } from "components/MprcIconButtonDropdown";
import { MprcRequestErrorDialog } from "components/MprcRequestErrorDialog";
import { PermissionConstraintDto, RolePermissionConstraintsDto } from "generated/CoreApi_v1";
import LibUtils from "lib/LibUtils";
import { useSnackbar } from "notistack";
import { useCoreApi } from "providers/CoreApiProvider";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { acceptedContraintsToConstraintTypes, addConstraint, constraintsAreEquals, ConstraintSelectionType, ConstraintTypes, convertToPermissionConstraintModel, deleteConstraint, getDefaultConstraintMenuItems, getPermissionConstraintMenuItems, RenderConstraints, selectConstraint } from "./EditRoleHelpers";

////////////////////////////////////////////////////////////////////////////

interface IProps {
    roleId: string;
    isReserved: boolean;
    userId: string;
    recordChanged: boolean;
    permissions: RolePermissionConstraintsDto[];
    onReload: () => void;
    onFormUpdate: (dto: RolePermissionConstraintsDto[]) => void;
}

////////////////////////////////////////////////////////////////////////////

/**
 * Edição das Permissões de um Papel Segurança
 * @param props
 */
export const EditRoleUserConstraints: React.FC<IProps> = (props) => {
    ////////////////////////////////////////////////////////////////////////////
    const { roleId, isReserved, userId, onReload, recordChanged, onFormUpdate } = props;
    const [drawer, setDrawer] = useState<any>();
    const [saving, setSaving] = useState<boolean>(false);
    const [viewError, setViewError] = useState<any>();
    const { enqueueSnackbar } = useSnackbar();

    const viewData = props.permissions;
    const canChange = !isReserved;

    const api = useCoreApi();
    const { t } = useTranslation();

    const canRestoreAll = viewData
        .filter(f => !constraintsAreEquals(f.customConstraints, f.defaultConstraints))
        .length > 0;

    ////////////////////////////////////////////////////////////////////////////

    ///
    const executeAddConstraint = (target: PermissionConstraintDto, ct: ConstraintTypes, selectedItem: any) => {
        switch (ct) {
            case "selfUserUnitTypes":
                if (selectedItem) {
                    if (!addConstraint(target, ct, selectedItem)) {
                        enqueueSnackbar(t("Role.Edit.User.Constraints.AlreadySelectedMessage", { name: selectedItem.name }), { variant: "warning" });
                    }
                }
                break;
            case "units":
                if (selectedItem) {
                    if (!addConstraint(
                        target,
                        ct,
                        {
                            unitId: selectedItem.unitId,
                            name: selectedItem.name,
                            unitTypeId: selectedItem.unitTypeId,
                            includeDescendants: selectedItem.includeDescendants,
                            linkedGroupId: selectedItem.linkedGroupId
                        }
                    )
                    ) {
                        enqueueSnackbar(t("Role.Edit.User.Constraints.AlreadySelectedMessage", { name: selectedItem.items[0].name }), { variant: "warning" });
                    }
                }
                break;
            case "groups":
                if (selectedItem) {
                    if (!addConstraint(target, ct, selectedItem))
                        enqueueSnackbar(t("Role.Edit.User.Constraints.AlreadySelectedMessage", { name: selectedItem.name }), { variant: "warning" });
                }
                break;
            default:
                addConstraint(target, ct, selectedItem);
                break;
        }
    };

    //
    const executeDeleteConstraint = (target: PermissionConstraintDto, ct: ConstraintTypes, selectedItem: any) => {
        deleteConstraint(target, ct, true, selectedItem);
    };

    ///
    const handleAddPermissionConstraint = (constraints: PermissionConstraintDto, ct: ConstraintTypes) => {
        selectConstraint(ct, true, setDrawer, t)
            .then(selectedItem => {
                executeAddConstraint(constraints, ct, selectedItem);
                onFormUpdate([...viewData]);
            });
    };

    ///
    const handleDeletePermissionConstraint = (constraints: PermissionConstraintDto, ct: ConstraintTypes, item: ConstraintSelectionType) => {
        deleteConstraint(constraints, ct, true, item || false);
        onFormUpdate([...viewData]);
    };

    ///
    const handleSave = () => {
        if (recordChanged) setSaving(true);
    };

    ///
    const handleRestore = () => {
        viewData.forEach((v) => v.customConstraints = LibUtils.deepCopy(v.defaultConstraints));
        onFormUpdate([...viewData]);
    };

    ///
    const handleUndo = (v: RolePermissionConstraintsDto) => {
        v.customConstraints = LibUtils.deepCopy(v.defaultConstraints);
        onFormUpdate([...viewData]);
    };

    ///
    const handleCancel = () => {
        onReload();
    };

    ///
    const handleAddForAll = (ct: ConstraintTypes) => {
        selectConstraint(ct, true, setDrawer, t)
            .then(selectedItem => {
                viewData.forEach((item) => {
                    if (acceptedContraintsToConstraintTypes(item.acceptedConstraints).includes(ct))
                        executeAddConstraint(item.customConstraints, ct, selectedItem);
                });
                onFormUpdate([...viewData]);
            });
    };

    ///
    const handleDeleteFromAll = (ct: ConstraintTypes) => {
        selectConstraint(ct, false, setDrawer, t)
            .then(selectedItem => {
                viewData.forEach((item) => {
                    if (acceptedContraintsToConstraintTypes(item.acceptedConstraints).includes(ct))
                        executeDeleteConstraint(item.customConstraints, ct, selectedItem);
                });
                onFormUpdate([...viewData]);
            });
    };

    ////////////////////////////////////////////////////////////////////////////
    /// Effect's
    ////////////////////////////////////////////////////////////////////////////

    // Ao salvar um DTO:
    useEffect(() => {
        async function postData() {
            try {
                var result = await api.v1.securityRoleManagement.saveUserConstraints({
                    roleId,
                    userId,
                    customPermissions: viewData
                        .map((v) => ({
                            permissionId: v.permissionId,
                            constraints: convertToPermissionConstraintModel(v.customConstraints)
                        }))
                }
                );

                if (result) onReload();
            }
            catch (e) {
                setViewError(e);
            }

            setSaving(false);
        }

        if (saving) postData();

    }, [saving]);

    ////////////////////////////////////////////////////////////////////////////
    /// RENDER
    ////////////////////////////////////////////////////////////////////////////

    //
    const RenderPermissions = () => {
        return (
            <Grid container direction="column">
                {
                    viewData.map((v, index) => {
                        const menuItems = getPermissionConstraintMenuItems(v.acceptedConstraints, v.customConstraints, t);
                        return (
                            <Grid size="grow"
                                key={index}
                                sx={
                                    (theme) => ({
                                        borderBottomStyle: "dashed",
                                        borderBottomWidth: 1,
                                        borderBottomColor: theme.palette.muted.light,
                                        mb: 1,
                                        flexGrow: 1,
                                        overflowy: "auto"
                                    })
                                }
                            >
                                <Grid container alignItems="center" color="muted.main" spacing={2}>
                                    <Grid>
                                        <MprcCardPrimaryText>
                                            {v.name}
                                        </MprcCardPrimaryText>
                                    </Grid>
                                    {
                                        canChange && !constraintsAreEquals(v.defaultConstraints, v.customConstraints) &&
                                        <Grid>
                                            <MprcLink title={t("Role.Edit.User.Constraints.RestorePermissionTooltip")} onClick={() => handleUndo(v)}>
                                                <IconUndo color="inherit" fontSize="16px" />
                                            </MprcLink>
                                        </Grid>
                                    }
                                </Grid>

                                <Grid container justifyContent="center" alignItems="center" width="100%" color="muted.main">
                                    {
                                        menuItems.length > 0 && canChange &&
                                        <Grid>
                                            <MprcIconButtonDropdown
                                                icon={<IconAdd />}
                                                onChange={(selection) => handleAddPermissionConstraint(v.customConstraints, selection)}
                                                items={getPermissionConstraintMenuItems(v.acceptedConstraints, v.customConstraints, t)}
                                            />
                                        </Grid>
                                    }
                                    <Grid size="grow">
                                        {
                                            menuItems.length == 0
                                                ? <Box sx={{ mb: 1 }}>
                                                    <i>{
                                                        !canChange
                                                            ? t("Role.Edit.Helpers.GlobalText")
                                                            : t("Role.Edit.User.Constraints.PermissionAcceptsNoConstraintsText")
                                                    }
                                                    </i>
                                                </Box>
                                                : v.customConstraints.global
                                                    ? <i>
                                                        {t(!canChange
                                                            ? "Role.Edit.Helpers.GlobalText"
                                                            : "Role.Edit.User.Constraints.AddConstraintsUsingPermissionText"
                                                        )}
                                                    </i>
                                                    : <RenderConstraints
                                                        constraints={v.customConstraints}
                                                        onDelete={canChange ? (ct, item) => handleDeletePermissionConstraint(v.customConstraints, ct, item) : undefined}
                                                    />
                                        }
                                    </Grid>
                                </Grid>
                            </Grid>
                        );
                    })
                }
            </Grid>
        );
    };

    //
    const RenderGridSubHeader = () => {
        return <MprcContainer>
            <Card elevation={2} sx={{ backgroundColor: "paper.dark", p: 1 }}>
                <MprcFormSubHeader
                    title={t("Role.Edit.User.Constraints.AddRemoveConstraintsTitle")}
                    subtitle={t("Role.Edit.User.Constraints.AddRemoveConstraintsSubtitle")}
                />

                <Grid container justifyContent="center" alignItems="center" width="100%" spacing={1} color="muted.main">
                    <Grid>
                        <MprcIconButtonDropdown
                            icon={<IconAdd />}
                            text={t("Role.Edit.User.Constraints.AddConstrainstButtonText")}
                            onChange={(selection) => handleAddForAll(selection)}
                            items={getDefaultConstraintMenuItems(t)}
                        />
                    </Grid>
                    <Grid>
                        <MprcIconButtonDropdown
                            icon={<IconSubtract />}
                            text={t("Role.Edit.User.Constraints.RemoveConstrainstButtonText")}
                            onChange={(selection) => handleDeleteFromAll(selection)}
                            items={getDefaultConstraintMenuItems(t)}
                        />
                    </Grid>
                </Grid>
            </Card>
        </MprcContainer>
            ;
    };

    //
    const RenderGrid = () => {
        return (
            <Grid container direction="column" height="100%">
                <Grid width="100%">
                    {canChange && <RenderGridSubHeader />}
                </Grid>

                <Grid size="grow" style={{ flexGrow: 1, overflowY: "hidden" }}>

                    <Grid container direction="column" height="100%">
                        <Grid size="grow" overflow="auto">

                            {
                                viewData.length == 0
                                    ? <Alert severity="info" >{t("Role.Edit.User.Constraints.NoPermissionsSetMessage")}</Alert>
                                    : <RenderPermissions />
                            }

                        </Grid>
                    </Grid>

                </Grid>
            </Grid>
        );
    };

    //
    const RenderActions = () => {
        return (
            <Grid container spacing={1}>
                <Grid size="grow">
                    {
                        canRestoreAll &&
                        <MprcButton
                            variant="contained"
                            onClick={handleRestore}
                        >
                            Usar restrições padrão
                        </MprcButton>
                    }
                </Grid>
                {
                    recordChanged &&
                    <Grid>
                        <ButtonCancel
                            variant="contained"
                            onClick={handleCancel}
                        />
                    </Grid>
                }
                <Grid>
                    <ButtonSave
                        disabled={!recordChanged}
                        onClick={handleSave}
                    />
                </Grid>
            </Grid>
        );
    };

    //
    return (
        <>
            {drawer}

            <DialogContent sx={{ pt: 0, height: "100vh" }}>

                <MprcRequestIndicator
                    visible={saving}
                    type={"post"}
                />

                {
                    viewError
                        ? <MprcRequestErrorDialog error={viewError} onClose={() => setViewError(null)} />
                        : <RenderGrid />
                }
            </DialogContent>

            {canChange &&
                <DialogActions>
                    <RenderActions />
                </DialogActions>
            }
        </>
    );
};
